import React from "react";
import { Music, Palette, Newspaper, Church, Calendar, HeartHandshake, Sparkles, BookOpenCheck } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const culturelle = [
  {
    icon: Music,
    titre: "Concerts du midi",
    texte: "Une scène ouverte sur la pause méridienne : élèves musiciens, artistes invités, groupes locaux animent régulièrement la cour ou la salle polyvalente.",
  },
  {
    icon: Palette,
    titre: "Projets artistiques",
    texte: "Ateliers de création, fresques participatives, expositions, partenariats avec des artistes en résidence : la culture nourrit le parcours de chacun.",
  },
  {
    icon: Newspaper,
    titre: "Rencontres avec des journalistes",
    texte: "Interventions de professionnels de la presse, ateliers d'éducation aux médias, débats sur l'information : aiguiser l'esprit critique des élèves.",
  },
  {
    icon: BookOpenCheck,
    titre: "Sorties culturelles",
    texte: "Théâtre, cinéma, musées, festivals : plusieurs sorties par an pour ouvrir l'horizon et découvrir le patrimoine angevin et au-delà.",
  },
];

const spirituelle = [
  {
    icon: Church,
    titre: "Pastorale",
    texte: "Une équipe de pastorale propose temps d'écoute, animations et célébrations rythmant l'année scolaire, dans le respect de chacun.",
  },
  {
    icon: Sparkles,
    titre: "Préparation aux sacrements",
    texte: "Baptême, première communion, confirmation : un cheminement personnalisé pour les jeunes qui le souhaitent, en lien avec les paroisses.",
  },
  {
    icon: Calendar,
    titre: "Marché de Noël & temps forts",
    texte: "Un marché de Noël solidaire animé par les élèves, des célébrations de l'Avent et de Pâques, des moments de partage ouverts à tous.",
  },
  {
    icon: HeartHandshake,
    titre: "Pèlerinages & marches",
    texte: "Marche itinérante, pèlerinage à Lourdes : des expériences fortes de fraternité, de solidarité et de découverte de soi.",
  },
];

export default function VieAuLycee() {
  return (
    <div data-testid="vie-au-lycee-page">
      <PageHero
        eyebrow="Vie au lycée"
        title="Bien plus qu'un lieu d'apprentissage : un lieu de vie"
        subtitle="Au lycée Joseph Wresinski, la formation professionnelle se conjugue avec une vie culturelle riche et une dimension spirituelle ouverte à tous, dans le respect des convictions de chacun."
        image={IMAGES.classroom}
      />

      <Section
        eyebrow="Vie culturelle"
        title="Faire grandir le regard et la sensibilité"
        subtitle="La culture est une voie d'épanouissement et d'ouverture. Tout au long de l'année, nos élèves découvrent des artistes, des œuvres, des métiers et des idées qui les inspirent."
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {culturelle.map((c, i) => (
            <Card key={c.titre} data-testid={`culturelle-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
                <c.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold text-brand-slate-dark">
                {c.titre}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.texte}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Vie spirituelle"
        title="Une pastorale ouverte, dans la tradition de Joseph Wresinski"
        subtitle="Notre projet d'établissement intègre une dimension spirituelle proposée à toutes et tous, dans le respect des croyances et du parcours personnel de chacun."
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {spirituelle.map((s, i) => (
            <Card key={s.titre} data-testid={`spirituelle-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sage/15 text-brand-sage-dark">
                <s.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold text-brand-slate-dark">
                {s.titre}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.texte}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-slate-dark text-brand-cream">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Une vie de lycée vivante
            </p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Des temps forts qui rythment l'année
            </h2>
            <p className="mt-5 text-base leading-relaxed text-brand-cream/85">
              Rentrée festive, célébration de l'Avent, marché de Noël solidaire, semaine de la culture,
              journée des talents, marche de fin d'année : autant d'occasions de se rassembler, de
              partager et de grandir ensemble.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Vie au lycée" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>
    </div>
  );
}
